import {
  faUser,
  // faCartShopping,
  faCalendarLines,
  faUsers,
  faHeart,
  // faBullhorn,
  faMessages,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Link } from '@/components/ui/link/link.component';
import { formatCustomerPreferredName } from '@/utils/formatters/format-customer-preferred-name';
import { authStore } from '@/store/auth.store';
import { useLogout } from '@/hooks/use-logout.hook';
import { phonePatientSac } from '@/utils/consts/sac.consts';
import { useWhatsappLinkWithAnalytics } from '@/hooks/use-whatsapp/use-whatsapp-link-with-analytics.hook';

export function ProfileSheet() {
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const logout = useLogout();
  const whatsappSacLink = useWhatsappLinkWithAnalytics(phonePatientSac, 'Olá! Sou paciente Medprev e preciso de suporte.');
  const whatsappSacUrl = whatsappSacLink.url;

  const profileItems = useMemo(() => [
    {
      category: 'Conta',
      items: [
        // { label: 'Carrinho', url: '/carrinho', icon: faCartShopping, isNew: false, testId: DataTestIdEnum.ITEM_CART },
        { label: 'Agendamentos', url: '/agendamentos', icon: faCalendarLines, isNew: false, testId: DataTestIdEnum.ITEM_APPOINTMENTS },
        { label: 'Perfil', url: '/meu-perfil', icon: faUser, isNew: false, testId: DataTestIdEnum.ITEM_PROFILE },
        { label: 'Dependentes', url: '/dependentes', icon: faUsers, isNew: false, testId: DataTestIdEnum.ITEM_DEPENDENTS },
        { label: 'Favoritos', url: '/favoritos', icon: faHeart, isNew: false, testId: DataTestIdEnum.ITEM_FAVORITES },
      ],
    },
    {
      category: 'Contato',
      items: [
        // { label: 'Feedback', url: '/feedback', icon: faBullhorn, isNew: false, testId: DataTestIdEnum.ITEM_FEEDBACK },
        { label: 'Fale com a gente', url: whatsappSacUrl, icon: faMessages, isNew: false, target: '_blank', testId: DataTestIdEnum.ITEM_HELP },
      ],
    },
  ], [whatsappSacUrl]);

  const userItems = useMemo(() => {
    return {
      name: authenticatedUser ? formatCustomerPreferredName(authenticatedUser)?.split(' ')[0] : 'Visitante',
      thumbnail: null,
    };
  }, [authenticatedUser]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid={DataTestIdEnum.HEADER_USER_BUTTON}
          className="bg-green-100 border border-gray-200 w-8 h-8 rounded-lg"
        >
          <Icon icon={faUser} className="text-green-500 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        role="menu"
        aria-label="Sheet content for user profile"
        side="right"
        closingTagPosition="right"
        className="w-full md:w-[22.5rem] text-gray-800 text-lg overflow-y-scroll px-0"
        data-testid={DataTestIdEnum.PROFILE_MENU_CONTENT}
      >
        <SheetHeader>
          <SheetTitle className="mb-8 mx-5">
            <div className="flex flex-row gap-4">
              <div className="text-gray-900 text-2xl text-left">
                <span>Olá,</span>
                {' '}
                <span className="font-bold">
                  {userItems.name}
                  !
                </span>
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <hr className="border border-gray-200 mb-6" />
        {profileItems.map((category) => (
          <div key={category.category} className="mt-6">
            <SheetDescription className="text-gray-600 uppercase text-lg font-light mb-3 mx-5">
              {category.category}
            </SheetDescription>
            <ul className="flex flex-col content-center !items-start">
              {category.items.map((item) => (
                <li
                  key={item.label}
                  className="flex flex-row  w-full focus-within:bg-gray-50 hover:bg-gray-50 text-gray-800 focus-within:text-success-700 hover:text-success-700 transition-all duration-300"
                >
                  <SheetClose asChild>
                    <Link
                      data-testid={item.testId}
                      prefetch={false}
                      role="menuitem"
                      href={item.url}
                      target={item.target}
                      className="flex items-center gap-2 py-4 w-full px-5"
                    >
                      <Icon icon={item.icon} className="w-5 h-5" />
                      <span className="text-lg font-normal">{item.label}</span>
                      {item.isNew && (
                        <div className="bg-green-100 text-success-700 text-base font-semibold border border-success-400 self-center px-2 rounded h-fit">
                          Novo
                        </div>
                      )}
                    </Link>
                  </SheetClose>
                </li>
              ))}
            </ul>
            <hr className="border border-gray-200 mt-8 " />
          </div>
        ))}
        <SheetFooter
          className=" text-gray-800 !justify-start px-5 focus-within:bg-gray-50 focus-within:text-success-700 hover:bg-gray-50 hover:text-success-700 transition-all duration-300 mt-4"
        >
          <SheetClose
            data-testid={DataTestIdEnum.LOGOUT}
            onClick={logout}
            className="text-left py-4"
          >
            <Icon icon={faArrowRightFromBracket} className=" w-5 mr-2" />
            <span className="text-lg ">Sair</span>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
